<template>
    <input-base :params="params" :inputFocused="inputFocused" :inputValue="inputValue === null ? '' : inputValue">
        <!-- Input slot -->
        <template v-slot:input>
            <!-- Input Currency -->
            <div
            ref="currencyRef"
            class="currency-container"
            :class="{ 'placeholder': !inputValue }">
                {{props.params.currency ? currencyParser(props.params.currency) : ''}}
            </div>
            <vue-format-amount-input
            :style="{'padding-left': `${currencyPaddingLeft}px`}"
            :value="inputValue"
            :options="amountInputOptions"
            :placeholder="params.placeholder || t('inputs.amountPlaceholder')"
            :disabled="false"
            @focus="[emit('onFocus'), inputFocused = true]"
            @blur="[emit('onBlur'), inputFocused = false]"
            @keyup.enter="emit('onEnter')"
            @input="emit('input', $event)"/>
        </template>
    </input-base>
</template>

<script setup>
import InputBase from '@/components/generic/inputs/Base'
/* Assets */
import { currencyParser } from '@/assets/scripts/amounts'
/* Vue Imports */
import { ref, computed, watch, onMounted, nextTick } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getDeviceInUse } = mapGetters()

/* Emmitters */
const emit = defineEmits(['onFocus', 'onBlur', 'onEnter', 'input'])

/** props received
@params {
    - labelText {String}: label for textField
    - placeholder {String}: text for placholder
    - currency {String}: Input Currency
    - mandatory { Boolean }
    - showCounter {Boolean}: property to display char counter
    - bottomMessage {String}: text to display in bottom elements
    - errorState {Boolean}: property to show if input has error or not
    - errorMessage {String}: text to display in bottom elements while in errorState
    - bottomElementsInLine { Boolean }
    - minValue {String, Number}
    - maxValue {String, Number}
}
@inputValue {String, Number}: value to be displayed in input
@allowNegativeValues {Boolean}: prop to allow negative amounts
*/
const props = defineProps({
    params: {
        type: Object,
        default: () => {
            return {}
        }
    },
    inputValue: [String, Number],
    errorState: Boolean,
	allowNegativeValues: Boolean
})

const inputFocused = ref(false)
const currencyRef = ref('')
const currencyInitialWidth = ref(0)
const currencyPaddingLeft = ref(24)

const amountInputOptions = computed(() => {
    const options = {
		currencySymbol: '',
		decimalChar: ',',
		digitGroupSeparator: ' ',
		showCurrencyOnFocus: true,
		showCurrencyOnHover: false,
		allowNegativeValues: !!props.params.allowNegativeValues,
        maxValue: '999999999999999.99',
        isIOS: getDeviceInUse.value === 'ios'
	}

    if (props.params.maxValue) options.maxValue = props.params.maxValue + ''

	return options
})

const calcCurrencySize = () => {
    currencyPaddingLeft.value = currencyRef.value && currencyRef.value.clientWidth ? currencyRef.value.clientWidth + 5 : 0
}

watch(() => inputFocused.value, (newVal, oldVal) => {
    calcCurrencySize()
    currencyPaddingLeft.value = currencyInitialWidth.value
})

watch(() => props.params.currency, (newVal, oldVal) => {
    calcCurrencySize()
})

onMounted(() => {
    nextTick(() => {
        /* Setting Currency Initial Width */
        calcCurrencySize()
        currencyInitialWidth.value = props.params.currency ? currencyRef.value.clientWidth + 5 : 0
    })
})
</script>

<style lang="scss" scoped>
input {
    width: 100%;
    height: 40px;

    @include H6-Regular;
    line-height: 40px;
    color: var(--secondary-dark-3);
}

.currency-container {
    height: 40px;
    display: block;
    position: absolute;
    @include H6-Regular;
    line-height: 40px;

    color: var(--secondary-dark-1);

    pointer-events: none;

    &.placeholder {
        color: var(--secondary);
    }
}
</style>
